import {
    type QuinnReplyTo,
    EMPTY_SOURCES,
    QuinnBusinessOption,
    QuinnChatSummary,
    QuinnCopy,
    QuinnInteractivePrompt,
    QuinnMessage,
    QuinnPromptOptionKind,
    QuinnRole,
    QuinnRuntime,
    QuinnRuntimeEvent,
    QuinnRuntimeEventType,
    QuinnScope,
    QuinnSendRequest,
    QuinnScopeMode,
    QuinnSourceBuckets,
    QuinnTraceStep,
    QuinnTraceStepKind,
    QuinnTraceStepStatus,
    createMessageId,
} from "../types";
import { ALL_CLIENTS_LABEL, NO_CLIENT_LABEL } from "../utils/constants.ts";
import type { QuinnApi, QuinnRunStatusDocumentSource, QuinnRunStatusPrompt } from "./useQuinnApi.ts";
import type {QuinnChatScopeEnum, QuinnRunStatusEnum, QuinnRunStepTypeEnum} from "../../../generated/enums.ts";

const ALL_CLIENTS_CHAT_SCOPE: QuinnChatScopeEnum = "all_clients";
const SINGLE_CLIENT_CHAT_SCOPE: QuinnChatScopeEnum = "single";

const RUN_POLL_INTERVAL_MS = 900;
const RUN_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const TERMINAL_RUN_STATUSES = new Set<QuinnRunStatusEnum>(["COMPLETED", "FAILED", "TIMED_OUT"]);
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

const STEP_KIND_BY_TYPE: Record<QuinnRunStepTypeEnum, QuinnTraceStepKind> = {
    REASONING: QuinnTraceStepKind.Reasoning,
    TOOL_CALL: QuinnTraceStepKind.SearchTransactions,
    TOOL_RESULT: QuinnTraceStepKind.Reasoning,
    ANSWER: QuinnTraceStepKind.Compose,
    ARTIFACT: QuinnTraceStepKind.Compose,
};

function isNotFoundError(error: unknown): boolean {
    return error instanceof Error && error.name === "Non200ResponseError" && (error as { status?: number }).status === 404;
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
        }
        const timer = setTimeout(() => {
            signal.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        function onAbort() {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
        }
        signal.addEventListener("abort", onAbort, { once: true });
    });
}

function makeStep(kind: QuinnTraceStepKind, label: string, detail: string | null = null): QuinnTraceStep {
    return {
        id: createMessageId(),
        kind,
        label,
        detail,
        status: QuinnTraceStepStatus.Running,
        durationMs: null,
        payload: null,
    };
}

function normaliseSources(raw: unknown): QuinnSourceBuckets {
    const buckets = Array.isArray(raw) ? raw : [];
    return {
        transactions: buckets.flatMap((bucket: any) => bucket?.transactions ?? []),
        documents: buckets.flatMap((bucket: any) => bucket?.documents ?? []),
        forecasts: buckets.flatMap((bucket: any) => bucket?.forecasts ?? []),
    };
}

function mapStatusSources(raw: QuinnRunStatusDocumentSource[] | null | undefined): QuinnSourceBuckets {
    return raw?.length ? {...EMPTY_SOURCES, documents: raw} : EMPTY_SOURCES;
}

function mapStatusPrompt(raw: QuinnRunStatusPrompt | null | undefined): QuinnInteractivePrompt | null {
    if (!raw?.question?.trim() || !Array.isArray(raw.options) || raw.options.length < 2) {
        return null;
    }

    const kind =
        raw.kind === QuinnPromptOptionKind.Business
            ? QuinnPromptOptionKind.Business
            : raw.kind === QuinnPromptOptionKind.Confirm
              ? QuinnPromptOptionKind.Confirm
              : QuinnPromptOptionKind.Choice;

    return {
        id: createMessageId(),
        kind,
        question: raw.question.trim(),
        helpText: raw.help_text?.trim() || null,
        options: raw.options.map((option) => ({
            value: option.value,
            label: option.label,
            hint: option.hint ?? null,
        })),
        multiple: Boolean(raw.multiple),
        allowFreeText: Boolean(raw.allow_free_text),
        answeredWith: null,
    };
}

function mapHistoryMessage(raw: any, rating?: string): QuinnMessage {
    const role = String(raw?.role ?? "").toLowerCase() === QuinnRole.User ? QuinnRole.User : QuinnRole.Quinn;
    const sources = normaliseSources(raw?.sources);

    return {
        id: typeof raw?.id === "string" ? raw.id : createMessageId(),
        role,
        content: raw?.content ?? "",
        createdAt: raw?.created_at ?? new Date().toISOString(),
        isStreaming: false,
        trace: [],
        sources,
        prompt: null,
        error: null,
        runId: typeof raw?.run_id === "string" ? raw.run_id : null,
        rating: rating === "up" || rating === "down" ? rating : null,
        replyTo: mapReplyTo(raw?.reply_to),
    };
}

function mapReplyTo(raw: unknown): QuinnReplyTo | null {
    if (!raw || typeof raw !== "object") return null;
    const stored = raw as Record<string, unknown>;
    if (typeof stored.artifact_id !== "string" || !stored.artifact_id) return null;
    return {
        artifactId: stored.artifact_id,
        kind: typeof stored.kind === "string" ? stored.kind : "",
        title: typeof stored.title === "string" ? stored.title : "",
        clientBusinessId: typeof stored.client_business_id === "string" ? stored.client_business_id : null,
        drawnForAllClients: stored.client_scope === ALL_CLIENTS_CHAT_SCOPE,
    };
}

function mapSessionToSummary(
    session: {
        id: string;
        title?: string | null;
        client_business_id?: string | null;
        client_scope?: string | null;
        last_message_at: string;
    },
    businesses: QuinnBusinessOption[],
): QuinnChatSummary {
    const business = businesses.find((entry) => entry.id === session.client_business_id);
    const coversAllClients = session.client_scope === ALL_CLIENTS_CHAT_SCOPE;

    return {
        id: session.id,
        title: session.title?.trim() || QuinnCopy.CurrentChat,
        updatedAt: session.last_message_at,
        mode: coversAllClients ? QuinnScopeMode.AllClients : QuinnScopeMode.Client,
        businessId: coversAllClients ? null : (session.client_business_id ?? null),
        businessLabel: coversAllClients ? ALL_CLIENTS_LABEL : (business?.name ?? NO_CLIENT_LABEL),
    };
}

export function createQuinnRuntime(api: QuinnApi): QuinnRuntime {
    let businessCache: QuinnBusinessOption[] = [];

    async function listBusinesses(): Promise<QuinnBusinessOption[]> {
        if (businessCache.length) return businessCache;
        businessCache = await api.listBusinesses();
        return businessCache;
    }

    async function* send(request: QuinnSendRequest): AsyncGenerator<QuinnRuntimeEvent, void, void> {
        const { signal, scope } = request;
        const startedAt = Date.now();
        const seenStepIds = new Set<string>();
        const openSteps = new Map<string, { id: string; startedAt: number }>();

        const interpretStep = makeStep(
            QuinnTraceStepKind.Interpret,
            "Vraag geïnterpreteerd",
            scope.businessLabel,
        );
        yield { type: QuinnRuntimeEventType.StepStarted, step: interpretStep };

        let started: { run_id: string; chat_id: string; status: string };
        try {
            started = await api.startRun({
                content: request.content,
                chatId: request.chatId,
                entrepreneurBusinessId: scope.businessId,
                clientScope: scope.mode === QuinnScopeMode.AllClients ? ALL_CLIENTS_CHAT_SCOPE : SINGLE_CLIENT_CHAT_SCOPE,
                replyToArtifactId: request.replyTo?.artifactId ?? null,
            });
        } catch (error: unknown) {
            if (signal.aborted) return;
            yield {
                type: QuinnRuntimeEventType.StepFinished,
                stepId: interpretStep.id,
                status: QuinnTraceStepStatus.Failed,
                detail: "Quinn kon niet starten",
            };
            yield {
                type: QuinnRuntimeEventType.Error,
                message:
                    error instanceof Error && error.message === "Failed to fetch"
                        ? "Geen verbinding met Quinn. Controleer je netwerk en probeer het opnieuw."
                        : QuinnCopy.GenericError,
            };
            return;
        }

        yield {
            type: QuinnRuntimeEventType.StepFinished,
            stepId: interpretStep.id,
            status: QuinnTraceStepStatus.Done,
            durationMs: Date.now() - startedAt,
        };

        if (started.chat_id && started.chat_id !== request.chatId) {
            yield { type: QuinnRuntimeEventType.ChatStarted, chatId: started.chat_id };
        }

        let reportedChatId = started.chat_id;
        let consecutivePollFailures = 0;
        const deadline = Date.now() + RUN_POLL_TIMEOUT_MS;
        let terminalStatus: string | null = null;
        let answer = "";
        let statusPrompt: QuinnRunStatusPrompt | null | undefined;
        let statusSources: QuinnRunStatusDocumentSource[] | undefined;

        while (Date.now() < deadline) {
            if (signal.aborted) return;

            let status;
            try {
                status = await api.getRunStatus(started.run_id);
            } catch (error: unknown) {
                if (signal.aborted) return;
                consecutivePollFailures += 1;
                if (consecutivePollFailures < MAX_CONSECUTIVE_POLL_FAILURES) {
                    await delay(RUN_POLL_INTERVAL_MS, signal);
                    continue;
                }
                for (const open of openSteps.values()) {
                    yield {
                        type: QuinnRuntimeEventType.StepFinished,
                        stepId: open.id,
                        status: QuinnTraceStepStatus.Failed,
                    };
                }
                yield {
                    type: QuinnRuntimeEventType.Error,
                    message:
                        error instanceof Error && error.message === "Failed to fetch"
                            ? "Geen verbinding met Quinn. Controleer je netwerk en probeer het opnieuw."
                            : QuinnCopy.GenericError,
                };
                return;
            }

            consecutivePollFailures = 0;

            if (status.chat_id && status.chat_id !== reportedChatId) {
                reportedChatId = status.chat_id;
                yield { type: QuinnRuntimeEventType.ChatStarted, chatId: status.chat_id };
            }

            for (const step of status.steps ?? []) {
                if (seenStepIds.has(step.id)) continue;
                seenStepIds.add(step.id);

                for (const [backendId, open] of [...openSteps.entries()]) {
                    yield {
                        type: QuinnRuntimeEventType.StepFinished,
                        stepId: open.id,
                        status: QuinnTraceStepStatus.Done,
                        durationMs: Date.now() - open.startedAt,
                    };
                    openSteps.delete(backendId);
                }

                const kind = STEP_KIND_BY_TYPE[step.step_type] ?? QuinnTraceStepKind.Reasoning;
                const local = makeStep(kind, step.label || "Bezig");
                openSteps.set(step.id, { id: local.id, startedAt: Date.now() });
                yield { type: QuinnRuntimeEventType.StepStarted, step: local };
            }

            if (TERMINAL_RUN_STATUSES.has(status.status)) {
                terminalStatus = status.status;
                answer = status.answer ?? "";
                statusPrompt = status.prompt;
                statusSources = status.sources;
                break;
            }

            await delay(RUN_POLL_INTERVAL_MS, signal);
        }

        for (const open of openSteps.values()) {
            yield {
                type: QuinnRuntimeEventType.StepFinished,
                stepId: open.id,
                status: QuinnTraceStepStatus.Done,
                durationMs: Date.now() - open.startedAt,
            };
        }

        if (!terminalStatus) {
            yield {
                type: QuinnRuntimeEventType.Error,
                message: "Quinn is nog bezig. Probeer het zo opnieuw.",
            };
            return;
        }

        if (terminalStatus !== "COMPLETED") {
            yield {
                type: QuinnRuntimeEventType.Error,
                message:
                    answer.trim() ||
                    "Quinn kon deze vraag niet afronden. Probeer het opnieuw.",
            };
            return;
        }

        if (answer) {
            yield { type: QuinnRuntimeEventType.AnswerDelta, text: answer };
        }

        const prompt = mapStatusPrompt(statusPrompt);

        yield {
            type: QuinnRuntimeEventType.AnswerFinished,
            sources: mapStatusSources(statusSources),
            prompt,
            runId: started.run_id,
        };
    }

    async function loadChat(chatId: string): Promise<QuinnMessage[] | null> {
        let response: { quinn_chat?: any; ratings?: Record<string, string> } | null;
        try {
            response = (await api.getChat(chatId)) as typeof response;
        } catch (error: unknown) {
            if (isNotFoundError(error)) return null;
            throw error;
        }
        const chat = response?.quinn_chat;
        if (!chat || chat.id !== chatId) return null;
        const ratings = response?.ratings ?? {};
        return (chat.message_history ?? []).map((raw: any) =>
            mapHistoryMessage(raw, raw?.run_id ? ratings[raw.run_id] : undefined),
        );
    }

    async function listChats(): Promise<QuinnChatSummary[]> {
        try {
            const [response, businesses] = await Promise.all([
                api.listSessions(),
                listBusinesses().catch(() => [] as QuinnBusinessOption[]),
            ]);
            return (response.sessions ?? []).map((session) => mapSessionToSummary(session, businesses));
        } catch {
            return [];
        }
    }

    async function openChat(chatId: string): Promise<QuinnMessage[] | null> {
        return loadChat(chatId);
    }

    return {
        send,
        listChats,
        openChat,
        listBusinesses,
        rateAnswer: api.rateAnswer,
    };
}
